import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { Target, ArrowRight } from "lucide-react";

export default function Phase4Scope() {
  const navigate = useNavigate();
  const { auditRun, advancePhase } = useAudit();
  const [controls, setControls] = useState([]);
  const [selected, setSelected] = useState([]);
  const [exclusions, setExclusions] = useState("");
  const [depth, setDepth] = useState("Standard");

  const fetchControls = useCallback(async () => {
    if (!auditRun?.id) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}/controls`);
    setControls(data);
    if (auditRun.scope) {
      setSelected(auditRun.scope.selected_controls || data.map(c => c.control_id));
      setExclusions((auditRun.scope.exclusions || []).join(", "));
      setDepth(auditRun.scope.depth_level || "Standard");
    } else {
      setSelected(data.map(c => c.control_id));
    }
  }, [auditRun]);

  useEffect(() => { fetchControls(); }, [fetchControls]);

  const toggleControl = (cid) => {
    setSelected(prev => prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]);
  };

  const handleSave = async () => {
    try {
      await API.put(`/audit-runs/${auditRun.id}/scope`, {
        selected_controls: selected,
        exclusions: exclusions.split(",").map(s => s.trim()).filter(Boolean),
        depth_level: depth
      });
      await advancePhase(5);
      navigate("/phase/5");
      toast.success("Scope defined. Proceeding to Artifact Collection.");
    } catch (e) { toast.error("Failed to save scope"); }
  };

  const areas = [...new Set(controls.map(c => c.mapped_process_area))];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="phase-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 4: Audit Scope Definition</h2>
          <p className="text-sm text-muted-foreground mt-1">Select control areas to audit and define exclusions.</p>
        </div>
        <Button onClick={handleSave} disabled={selected.length === 0} data-testid="save-scope-top-btn" className="sm:shrink-0">
          <ArrowRight className="w-4 h-4 mr-2" /> Save & Continue
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Control Areas</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {areas.map(area => {
            const areaControls = controls.filter(c => c.mapped_process_area === area);
            const allSelected = areaControls.every(c => selected.includes(c.control_id));
            return (
              <div key={area} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() => {
                      if (allSelected) {
                        setSelected(prev => prev.filter(id => !areaControls.map(c => c.control_id).includes(id)));
                      } else {
                        setSelected(prev => [...new Set([...prev, ...areaControls.map(c => c.control_id)])]);
                      }
                    }}
                  />
                  <Label className="font-medium text-sm">{area}</Label>
                  <Badge variant="outline" className="text-xs font-mono">{areaControls.length}</Badge>
                </div>
                <div className="ml-6 space-y-1">
                  {areaControls.map(c => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={selected.includes(c.control_id)}
                        onCheckedChange={() => toggleControl(c.control_id)}
                        data-testid={`scope-ctrl-${c.control_id}`}
                      />
                      <span className="text-xs font-mono text-muted-foreground w-16">{c.control_id}</span>
                      <span className="text-xs">{c.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <Label className="text-xs">Exclusions (comma-separated)</Label>
            <Input value={exclusions} onChange={e => setExclusions(e.target.value)}
              placeholder="e.g., IT Controls, Physical Security" className="mt-1" data-testid="exclusions-input" />
          </div>
          <div>
            <Label className="text-xs">Depth Level</Label>
            <Select value={depth} onValueChange={setDepth}>
              <SelectTrigger className="mt-1" data-testid="scope-depth-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Limited">Limited</SelectItem>
                <SelectItem value="Standard">Standard</SelectItem>
                <SelectItem value="Comprehensive">Comprehensive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Selected: <span className="font-mono font-semibold text-foreground">{selected.length}</span> / {controls.length} controls
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={selected.length === 0} data-testid="save-scope-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Save & Continue
        </Button>
      </div>
    </div>
  );
}
